import { useState, useEffect } from "react";
import dayjs from "dayjs";
import Constants, { type ConstantsEnum } from "data/constants";

const getLocalDate = (language: string | null): string => {
    if (language == null) {
        return "de";
    }
    switch (language) {
        case "en":
        case "en-US":
            return "en";
        case "de-DE":
            return "de";
        case "zh-Hans":
            return "zh-cn";
        default:
            return language.toLowerCase();
    }
};

export const loadedPlugins: Set<string> = new Set();
export const usedPlugins: string[] = [
    "duration",
    "relativeTime",
    "localeData",
    "localizedFormat",
    "customParseFormat",
    "isStrictValid",
    "dotnetFormats",
];

const useLocalDate = (language: string): ConstantsEnum => {
    const locale = getLocalDate(language);
    const [status, setStatus] = useState<ConstantsEnum>(Constants.LOADING);

    useEffect(() => {
        if (
            usedPlugins.every((plugin) => loadedPlugins.has(plugin)) &&
            dayjs.locale() === locale
        ) {
            return setStatus(Constants.OK);
        }

        Promise.all([
            ...usedPlugins.map((plugin) =>
                import(
                    `data/dayjs/plugin/${plugin}` /* webpackChunkName: "dayjs-[request]" */
                )
                    .then(async (loadedPlugin) => {
                        dayjs.extend(loadedPlugin.default);
                        loadedPlugins.add(plugin);
                        if (plugin === "dotnetFormats") {
                            await import(
                                `data/dayjs/format/${locale}` /* webpackChunkName: "dayjs-[request]" */
                            )
                                .then((format) => {
                                    dayjs.formats(format.default);
                                })
                                .catch((e) => {
                                    console.error(
                                        "[useLocalDate] Cannot set formats for day.js:",
                                        e
                                    );
                                    throw e;
                                });
                        }
                    })
                    .catch((e) => {
                        console.error(
                            "[useLocalDate] Cannot load day.js plugin:",
                            e
                        );
                        throw e;
                    })
            ),
            new Promise<void>((resolve, reject) => {
                if (locale === "en") {
                    resolve();
                    return;
                }
                import(
                    `__i18n/dayjs/${locale}` /* webpackChunkName: "dayjs-[request]" */
                )
                    .then(() => {
                        dayjs.locale(locale);
                        resolve();
                    })
                    .catch((e) => {
                        console.error(
                            "[useLocalDate] Cannot load day.js locale:",
                            e
                        );
                        reject(e);
                    });
            }),
        ])
            .then(() => {
                setStatus(Constants.OK);
            })
            .catch((e) => {
                console.error("[useLocalDate] Cannot load day.js:", e);
                setStatus(Constants.ERROR);
            });
    }, [locale]);

    return status;
};

export { getLocalDate, useLocalDate };
